import { useEffect, useState } from "react";
import { Button, InfoModal, useModal } from "@newsela/angelou";
import {
  actionPrefixes,
  actionNames,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { useRouter } from "next/router";
import useHasFeature from "@/features/useHasFeature";
import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import { ExportablesGenerationResponse } from "@/types/models/Exportables.interface";
import AILoadingSpinner from "@/components/AIComponents/AILoadingSpinner";

import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";
import {
  buttonRootCss,
  modalRootCss,
  modalContentWrapperCss,
  scrollableBodyCss,
  modalFooterCss,
  warningTextCss,
  loadingWrapper,
  loadingSpinnerWrapper,
} from "./ExportablesModal.styles";
import AISpark from "./AISpark.svg";
import ExportablesEditor from "./ExportablesEditor";
import DownloadPDFButton from "./ExportablesEditor/DownloadPDFButton";
import { TemplateDefinition, TEMPLATE_REGISTRY } from "./templates/registry";

const ButtonWithTrackEvent = withTrackEvent(Button);

export const GRAPHIC_ORGANIZER_PARAM = "graphicOrganizer";

interface ExportablesModalProps {
  contentId: string;
  exportablesMetadata?: ExportablesGenerationResponse | undefined;
  exportablesLoading?: boolean | null;
  articleShortTitle?: string | undefined;
}

const ExportablesModal = ({
  contentId,
  exportablesMetadata,
  exportablesLoading,
  articleShortTitle,
}: ExportablesModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const [modalProps, setActive, triggerRef] = useModal(false);
  const [selectedExportable, setSelectedExportable] =
    useState<TemplateDefinition<any> | null>(null);
  const [formData, setFormData] = useState<any>(null);
  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  useEffect(() => {
    setIsLoading(exportablesLoading || exportablesMetadata === null);
  }, [exportablesMetadata]);

  useEffect(() => {
    const { query } = router;
    const graphicOrganizerParam = query[GRAPHIC_ORGANIZER_PARAM];

    if (graphicOrganizerParam && typeof graphicOrganizerParam === "string") {
      const template = Object.values(TEMPLATE_REGISTRY).find(
        (t) => t.seti_identifier === graphicOrganizerParam,
      );

      if (template) {
        setSelectedExportable(template);
        setActive(true);
      }
    }
  }, [router.query, setActive]);

  useEffect(() => {
    if (
      modalProps.isOpen &&
      !selectedExportable &&
      Object.keys(TEMPLATE_REGISTRY).length > 0
    ) {
      const defaultTemplate = Object.values(TEMPLATE_REGISTRY)[0];
      setSelectedExportable(defaultTemplate);
    }
  }, [modalProps.isOpen, selectedExportable]);

  const handleTemplateSelection = (
    template: TemplateDefinition<any> | null,
  ) => {
    if (!template) {
      setSelectedExportable(null);
      return;
    }

    setSelectedExportable(template);

    const { pathname, query } = router;
    const newQuery = {
      ...query,
      [GRAPHIC_ORGANIZER_PARAM]: template.seti_identifier,
    };

    router.replace(
      {
        pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
  };

  const handleOpenModal = () => {
    setActive(true);
  };

  const handleModalClose = () => {
    setActive(false);

    const { pathname, query } = router;
    const newQuery = { ...query };
    delete newQuery[GRAPHIC_ORGANIZER_PARAM];

    router.replace(
      {
        pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
  };

  const eventProperties = {
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.interact,
    objectType: objectTypes.content,
    streamProperties: {
      description: `Open modal for exportable resources`,
      content_id: contentId,
    },
  };

  return (
    <>
      <ButtonWithTrackEvent
        __cssFor={{ root: buttonRootCss }}
        icon={{
          SvgComponent: AISpark,
          alignment: Button.iconAlignments.right,
        }}
        isFullWidth
        flavor={Button.flavors.primary}
        onClick={handleOpenModal}
        ref={triggerRef}
        dataProps={{
          "data-qa-selector": "open-exportables-modal-button",
        }}
        {...eventProperties}
      >
        Generate graphic organizers
      </ButtonWithTrackEvent>
      <InfoModal
        __cssFor={{
          root: modalRootCss,
          modalContentWrapper: modalContentWrapperCss,
          scrollableBody: scrollableBodyCss,
        }}
        closeButtonProps={{
          dataProps: { "data-qa-selector": "close-exportables-modal-button" },
        }}
        eyebrow={showLunaAiBranding ? <AILunaTag /> : "Newsela AI Tools"}
        isMobileTakeover
        labelId="exportables-modal"
        onBackgroundClick={handleModalClose}
        title="Generate graphic organizers"
        footer={
          !isLoading && (
            <div css={modalFooterCss}>
              <p css={warningTextCss} data-qa-selector="ai-warning-text">
                AI can make mistakes. Be sure to check AI-generated content
                before using.
              </p>
              <DownloadPDFButton
                selectedExportable={selectedExportable}
                formData={formData}
              />
            </div>
          )
        }
        {...modalProps}
      >
        {isLoading ? (
          <div css={loadingWrapper}>
            <AILoadingSpinner __cssFor={{ root: loadingSpinnerWrapper }} />
          </div>
        ) : (
          <ExportablesEditor
            exportablesMetadata={exportablesMetadata}
            selectedExportable={selectedExportable}
            setSelectedExportable={handleTemplateSelection}
            formData={formData}
            setFormData={setFormData}
            articleShortTitle={articleShortTitle}
          />
        )}
      </InfoModal>
    </>
  );
};

export default ExportablesModal;
